import React from 'react';
import { get, size, find, noop, map } from 'lodash';
import { withRouter } from 'react-router-dom';
import searchQuery from 'query-string';
import * as backRoute from 'utils/backRoute';

// local dependencies
import { memberDetailsPrescriptionsEntityWidget as MemberDetailsPrescriptionsEntityWidgetView } from 'ui/views/widgets/memberDetailsPrescriptionsEntityWidget';
import { MemberDetailsPrescriptionsEntityWidgetService } from 'services/memberDetailsPrescriptionsEntityWidget';
import { SERVICE_RESPONSE_STATUS, ORIGIN, GLOBAL_LABELS, ERROR_RESPONSE_TEMPLATE, SIZES } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import { getCheckoutState, addRxToCheckoutState, updateRxState } from 'resolvers/checkoutState';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';

// import * as notificationOverlay from 'utils/notificationOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsPrescriptionsEntityWidget } from 'ui/containers/MemberDetailsPrescriptionsEntityWidget';
 *
 * // render
 * <MemberDetailsPrescriptionsEntityWidget propName='propValue'/>
 */
export class _MemberDetailsPrescriptionsEntityWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            isCheckingLiveStatus: false,
            liveStatusAsOf: null,
            liveStatusError: null
        };

        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleUpdateSlection = this.handleUpdateSlection.bind( this );
        this.initiateChangeNextRefillDate = this.initiateChangeNextRefillDate.bind( this );
        this.submitChangeNextRefillDate = this.submitChangeNextRefillDate.bind( this );
        this.fetchLivePrescriptionsStatus = this.fetchLivePrescriptionsStatus.bind( this );

        // member prescriptions instance
        this.memberDetailsPrescriptionsEntityWidgetService = new MemberDetailsPrescriptionsEntityWidgetService();

        // start member detail data fetch
        this.memberDetailsPrescriptionsEntityWidgetData();
    }

    async memberDetailsPrescriptionsEntityWidgetData() {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.memberDetailsClaimsEntityWidgetData()' );

        // cancel ongoing request
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        try {
            const { status, data }  = await this.memberDetailsPrescriptionsEntityWidgetService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {

                
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }
    
    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {}, selection = [] } = {}, callback = noop ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.handleActionClick()'  );

        // get member id
        const memberId = get( this.props, 'requestPayload.context.memberId', null );

         // get member id
         const patientId = get( this.props, 'requestPayload.context.patientId', null );
        const prescriptionId = get( item, 'id', null );

        // prepare back-route state payload
        const backRouteState = backRoute.encode( {
            pathname: this.props.location.pathname,
            payload: {
                entitiesActiveTabIndex: this.props.tabIndex,
                memberId: memberId,
                patientId: patientId
            },
        } );
        
        if(  'discontinueRx' === value.name ) {
            return this.initiateDiscontinueRxProcess( {
                url: value.url,
                payload: {
                    epostRxNumber: item.values.rxNumber,
                    epostRxId: item.values.refillSequenceNumber,
                    selection,
                    patientId,
                    memberId
                },
                backRouteState
            } );
        }

        if( 'selection' === type && 'checkout' === value.name ) {
            return this.initiateCheckoutProcess( {
                url: value.url,
                payload: {
                    memberId,
                    selection,
                    patientId
                },
                backRouteState
            } );
        }

        if( 'column' === type && 'rxNumber' === value.name ) {
            
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.PRESCRIPTION_DETAIL,
                state: { ...backRouteState,  memberId: memberId, patientId: patientId },
                search: searchQuery.stringify( {
                    rxId: item.id,
                } )
            } );
        }

        if ( 'groupAction' === type && 'enableAuto' === value.name ) {
            
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.ENABLE_AUTO_REFILL,
                search: searchQuery.stringify( {
                    rxId: prescriptionId,
                    origin: ORIGIN.MEMBER_MANAMGENT
                } ),
                state: { memberId: memberId, patientId: patientId }
            } );
        }

        if ( 'groupAction' === type && 'disableAuto' === value.name ) {
            return this.initiateDisableAuto( {
                url: value.url,
                payload: {
                    data: {
                        memberId,
                        patientId,
                        rxId: prescriptionId,
                    }
                },
            }, callback );
        }

        if ( 'groupAction' === type && 'toDos' === value.name ) {
            return this.fetchToDoWidgetInfo( {
                action: value,
                payload: {
                    memberId,
                    patientId,
                    rxId: prescriptionId,
                }
            } );
        }

        if ( 'cell' === type && 'nextRefillDate' === value.name ) {
            const cellAction = find( get( this.state.componentData, 'structure.actionsConfig.cellActions' ), { name: value.name } );

            return this.initiateChangeNextRefillDate( {
                cellAction,
                payload: {
                    memberId,
                    patientId,
                    rxId: prescriptionId
                }
            }, callback );
        }
    }

    /**
     * @desc Shows an overlay to change next refill date
     */
    async initiateChangeNextRefillDate( { cellAction, payload }, callback ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.initiateChangeNextRefillDate()' );

        // cancel ongoing service
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch overlay form widget data
        const { status, data } = await this.memberDetailsPrescriptionsEntityWidgetService.fetchEditNextRefillDateInfo( {
            url: get( cellAction, 'url' ),
            data: {
                context: {
                    memberId: get( payload, 'memberId' ),
                    patientId: get( payload, 'patientId' )
                },
                data: {
                    rxId: get( payload, 'rxId' )
                }
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // open overlay with received widget data
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CHANGE_NEXT_REFILL_DATE,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    payload,
                    callback,
                    onFormSubmit: this.submitChangeNextRefillDate,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open();
        }
    }

    /**
     * @desc Submits next refill date change request
     */
    async submitChangeNextRefillDate( { actions, payload, formData, callback } ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.submitChangeNextRefillDate()' );

        // cancel ongoing request
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        // close overlay
        overlay.close();

        // show page level preloader
        pageLevelPreloader.show();

        // find `save` action
        const saveAction = find( actions, { name: 'save' }, {} );

        // submit request and get response status
        const { status, data } = await this.memberDetailsPrescriptionsEntityWidgetService.submitChangeNextRefillDate( {
            url: saveAction.url,
            data: {
                context: {
                    memberId: get( payload, 'memberId' ),
                    patientId: get( payload, 'patientId' )
                },
                data: {
                    rxId: get( payload, 'rxId' ),
                    ...formData
                }
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update state
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // prepare payload for row refresh
                const requestPayloadForRefresh = {
                    context: {
                        memberId: get( payload, 'memberId' ),
                        patientId: get( payload, 'patientId' )
                    },
                    data: {
                        rxId: get( payload, 'rxId' )
                    }
                };

                // invoke callback (table row refresh function) with rx id and above prepared payload
                callback( { id: payload.rxId, requestPayloadForRefresh } );
            } );
        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            // if template's available in error response
            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {

                // show template error overlay
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {

                // show default error overlay
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc: Action executed  when MSR clicks on the To-Dos icon
     */
    async fetchToDoWidgetInfo( { action, payload } ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.fetchToDoWidgetInfo()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        try {
            const { status, data } = await this.memberDetailsPrescriptionsEntityWidgetService.fetchToDoWidgetInfo( {
                url: action.url,
                data: {
                    context: {
                        memberId: payload.memberId,
                        patientId: payload.patientId,
                        rxId: payload.rxId
                    }
                }
            } );

            pageLevelPreloader.hide();

            // check if response status was success
            if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                errorOverlay.open();

            } else {

                // open overlay and send the `data`
                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.TO_DO_PRESCRIPTION,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    data: {
                        payload,
                        ...action,
                        ...data,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            }

        } catch ( e ) {

            pageLevelPreloader.hide();

            errorOverlay.open();
        }
    }
    
    /**
     * @desc: Action executed  when MSR clicks on the Disable Auto icon
     */
    async initiateDisableAuto( { url, payload }, callback ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.initiateDisableAuto()' );

        // cancel ongoing request
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailsPrescriptionsEntityWidgetService.disableAutoData( {
            url: url,
            context: {
                memberId: payload.data.memberId,
                patientId: payload.data.patientId
            },
            data: payload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // prepare back-route state payload
        const backRouteState = backRoute.encode( {
            pathname: this.props.location.pathname,
            payload: {
                entitiesActiveTabIndex: this.props.tabIndex,
                memberId: payload.data.memberId,
                patientId: payload.data.patientId
            },
        } );

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.DISABLE_AUTO_REFILL,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    backRouteState: backRouteState,
                    history: this.props.history,
                    payload: payload,
                    routeChange: true,
                    onSumbitClick: async () => {

                        overlay.close();

                        // find action with `name: submit`
                        const action = find( get( data, 'actions', [] ), { name: 'submit' } );

                        // cancel ongoing request
                        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

                        // show page level preloader
                        pageLevelPreloader.show();

                        // get status and service data
                        const { _status, _data } = await this.memberDetailsPrescriptionsEntityWidgetService.enableAuto( {
                            url: action.url,
                            data: {
                                context: {
                                    memberId: payload.data.memberId,
                                    patientId: payload.data.patientId
                                },
                                data: {
                                    rxId: payload.data.rxId,
                                    enableAuto: false,
                                },
                            },
                        } );

                        // show page level preloader
                        pageLevelPreloader.hide();

                        // check if response status was success
                        if( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            // display success overlay
                            successOverlay.open( {
                                message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                            } );


                            this.setState( {
                                isFetching: false,
                                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                            } );
            
                            const requestPayloadForRefresh = {
                                'context': {
                                  'memberId': payload.data.memberId,
                                  'patientId': payload.data.patientId
                                },
                                'data': {
                                  'rxId': payload.data.rxId
                                }
                            };
            
                            callback( { id: payload.data.rxId, requestPayloadForRefresh: requestPayloadForRefresh } );

                        } else {

                            const template = get( _data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

                            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                                errorOverlay.open( { message: _data.description, size: SIZES.SMALL } );
                            } else {
                                errorOverlay.open();
                            }

                            // errorOverlay.open(); // show error
                        }

                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * @desc: Action executed  when MSR clicks DiscontinueRx Icon
     */
    async initiateDiscontinueRxProcess( { url, payload } ) {
        log.debug( 'MemberDetailsOrdersEntityWidget.initiateDiscontinueRxProcess()' );

        const discontinueRxConfirmConfig = get( this.state, 'componentData.structure.discontinueRxConfirmConfig', {} );


        overlay.open( {
            settings: {
                modalType: OVERLAY_VIEWS.CONFIRM_VIEW,
                showCloseButton: false,
                ignoreOutsideClick: true,
            },
            config: {},
            data: {
                title: getLabel( get( discontinueRxConfirmConfig, 'title', {} ) ),
                message: getLabel( get( discontinueRxConfirmConfig, 'message', {} ) ),
                iconName: 'danger',
                confirmButtonLabel: get( discontinueRxConfirmConfig, 'confirmButton', {} ),
                cancelButtonLabel: get( discontinueRxConfirmConfig, 'cancelButton', {} ),
                onYesClick: async() => {
                    overlay.close();
                    await this.proceedWithDiscontinueRx( { url, payload } );
                },
                onNoClick: () => {
                    overlay.close();
                }
            },
        } );
    }

    /**
     * @desc: Proceed with discontinue Rx after confirmation
     */
    async proceedWithDiscontinueRx( { url, payload } ) {
        log.debug( 'MemberDetailsOrdersEntityWidget.proceedWithDiscontinueRx()' );

        // cancel ongoing request
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.memberDetailsPrescriptionsEntityWidgetService.newOrder( {
            url: url,
            data: {
                "context": {
                    "memberId": payload.memberId,
                    "patientId": payload.patientId,
                    "callId": payload.callId,
                    "callerType": payload.callerType,
                    "callType": payload.callType
                },
                "data": {
                    "epostRxNumber": payload.epostRxNumber,
                    "epostRxId": payload.epostRxId
                }
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {
                // console.log('success')
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                    config: {
                        autoClose: false
                    }
                } );
            } );
        } else {
            errorOverlay.open( { message: data?.description, size: SIZES.SMALL }); // show error
        }
    }


    /**
     * @desc: Action executed  when MSR clicks on the Cart icon
     */
    async initiateCheckoutProcess( { url, payload } ) {
        log.debug( 'MemberDetailsOrdersEntityWidget.initiateCheckoutProcess()' );

        // cancel ongoing request
        this.memberDetailsPrescriptionsEntityWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // update store with `memberId` and selected id's for new order
        addRxToCheckoutState( { memberId: payload.memberId, rxId: payload.selection, patientId: payload.patientId } );

        // fetch component data
        const { status } = await this.memberDetailsPrescriptionsEntityWidgetService.newOrder( {
            url: url,
            data: {
                context: {
                    memberId: payload.memberId,
                    patientId: payload.patientId
                },
                data: {
                    rxIds: payload.selection,
                },
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.NEW_ORDER,
                    search: searchQuery.stringify( {
                        origin: ORIGIN.MEMBER_MANAMGENT
                    } ),
                    state: { memberId: payload.memberId, patientId: payload.patientId }
                } );
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.componentWillUnmount()' );

        this.memberDetailsPrescriptionsEntityWidgetService.cancel(); // cancel ongoing requests
    }

    handleUpdateSlection( { values } ) {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.handleUpdateSlection()' );

        const memberId = get( this.props, 'requestPayload.context.memberId', null );
        const patientId = get( this.props, 'requestPayload.context.patientId', null );
        updateRxState( { memberId,patientId, rxId: values } );
    }

    /**
     * @desc Fetch live status for every prescription currently displayed, bypassing the
     * ES/logstash cache, and replace the list with the freshly-fetched data.
     */
    async fetchLivePrescriptionsStatus() {
        log.debug( 'MemberDetailsPrescriptionsEntityWidget.fetchLivePrescriptionsStatus()' );

        const rxNumbers = map(
            get( this.state, 'componentData.data.state.items', [] ),
            ( item ) => get( item, 'values.rxNumber' )
        ).filter( Boolean );

        if ( 0 === rxNumbers.length ) {
            this.setState( { liveStatusError: 'FETCH_FAILED' } );
            return;
        }

        this.setState( { isCheckingLiveStatus: true, liveStatusError: null } );

        const { status, data, httpStatus } = await this.memberDetailsPrescriptionsEntityWidgetService.fetchLivePrescriptionsStatus( {
            data: {
                ...this.props.requestPayload,
                data: { rxNumbers },
            },
        } );

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            const liveItems = get( data, 'data.state.items', [] );
            this.setState( ( prevState ) => ( {
                isCheckingLiveStatus: false,
                liveStatusAsOf: new Date().toISOString(),
                liveStatusError: null,
                componentData: {
                    ...prevState.componentData,
                    data: {
                        ...get( prevState, 'componentData.data', {} ),
                        state: {
                            ...get( prevState, 'componentData.data.state', {} ),
                            items: liveItems,
                        },
                    },
                },
            } ) );
        } else {
            const errorType = httpStatus === 404 ? 'STATUS_UNAVAILABLE' : 'FETCH_FAILED';
            this.setState( { isCheckingLiveStatus: false, liveStatusError: errorType } );
            console.error( 'fetchLivePrescriptionsStatus failed' );
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {

        const { rxIds } = getCheckoutState();

        return (
            <MemberDetailsPrescriptionsEntityWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                requestPayload={ this.props.requestPayload }
                onActionClick={ this.handleActionClick }
                selectedRxId={ 0 !== size( rxIds ) ? rxIds: undefined }
                onUpdateSelectionValues={ this.handleUpdateSlection }
                isCheckingLiveStatus={ this.state.isCheckingLiveStatus }
                liveStatusAsOf={ this.state.liveStatusAsOf }
                liveStatusError={ this.state.liveStatusError }
                onCheckLiveStatus={ this.fetchLivePrescriptionsStatus }
            />
        );
    }
}

// set display name
_MemberDetailsPrescriptionsEntityWidget.displayName = '_MemberDetailsPrescriptionsEntityWidget';

// set default props
_MemberDetailsPrescriptionsEntityWidget.defaultProps = {};

// prop types
_MemberDetailsPrescriptionsEntityWidget.propTypes = {};

export const MemberDetailsPrescriptionsEntityWidget = withRouter( _MemberDetailsPrescriptionsEntityWidget );
